// campaign-draft-creator.tsx — the `/campaigns/new` action.
//
// Creates a fresh `Drip` with `status:'draft'` (an empty single-step shell) and
// REDIRECTS to `/campaigns/:id/edit/audience`. From that point on, "new" and
// "edit existing" are the same flow — the wizard always loads a draft by id and
// autosaves to it. A `replace` redirect keeps the throwaway `/new` URL out of
// the browser history (Back from step 1 goes to the list, not back to /new →
// which would create yet another draft).

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { namespace } from '@common/config/constants';

import { useAppDispatch } from '@common-reducers/hooks/store.hook';
import { DBDripThunks } from '@common-reducers/DBServiceThunks';
import type { Drip } from '@bt-common/schemas/bt/drip';
import { flowPath } from '@common/utils/clear-flow';

export const CampaignDraftCreator: React.FC = () => {
  const { t } = useTranslation(namespace);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  // Guard against React 18 StrictMode's double-invoke (and any re-render) so we
  // only ever create ONE draft per visit to /campaigns/new.
  const startedRef = React.useRef(false);

  React.useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    (async () => {
      try {
        const draft: Partial<Drip> = {
          name: '',
          status: 'draft',
          boardIDArray: [],
          steps: [],
          // OFF by default — a group recipient exits on any member's post, so an
          // on-by-default exit silently drops groups from every remaining step.
          stopOnReply: false,
          currentStep: 0,
          schedule: { isRecurring: false, isTimeValid: false },
        };
        const created = await dispatch(DBDripThunks.create(draft as Drip)).unwrap();
        const newId = created?._id;
        if (typeof newId !== 'string') throw new Error('Drip create returned no id');
        navigate(flowPath(`/campaigns/${newId}/edit/audience`), { replace: true });
      } catch (err) {
        console.error('[CampaignDraftCreator] failed to create draft', err);
        navigate(flowPath('/campaigns'), { replace: true });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="col" style={{ flex: 1, alignItems: 'center', justifyContent: 'center', background: 'var(--bg)' }}>
      <div className="fade-in" style={{ fontSize: 13, color: 'var(--muted)' }}>{t('campaignsRedesign.creatingCampaign', 'Creating campaign…')}</div>
    </div>
  );
};

export default CampaignDraftCreator;
